/**
 * Typed client for WP1-SCHED-API (`/v1/schedule/*`). Every response is
 * validated against the contract (`@velora/contracts/schedule` plus the
 * mirrored WP1-SCHED-API section in `./contract.ts`), so drift fails loudly
 * instead of drawing a wrong schedule.
 */
import { PatientSearchResponseSchema } from '@velora/contracts/patients';
import {
  AsapEntryResultSchema,
  AsapTextPreviewSchema,
  AsapTextSendResultSchema,
  CompoundCreateResultSchema,
  FillCandidatesResultSchema,
  HouseholdSchema,
  HouseholdSlotsResultSchema,
  ListChangeResultSchema,
  ListContactResultSchema,
  ScheduleRangeSchema,
  SlotCheckResultSchema,
  TransferResultSchema,
  type TransferRequest,
  type TransferResult,
  SlotLeaseSchema,
  type MoveRequest,
  type ScheduleRange,
  type SlotCheckRequestInput,
  type SlotCheckResult,
  type SlotLease,
  type SlotLeaseRequestInput,
  SeriesResultSchema,
  PmsHandoffResultSchema,
  SavedViewsResultSchema,
  ScheduleSavedViewSchema,
  type PmsHandoffRequestInput,
  type PmsHandoffResult,
  type SavedViewCreateInput,
  type SavedViewPatchInput,
  type SavedViewsResult,
  type ScheduleSavedView,
  type AsapEntryCreateInput,
  type AsapEntryResult,
  type AsapTextOpening,
  type AsapTextPreview,
  type AsapTextSendRequest,
  type AsapTextSendResult,
  type CompoundCreateResult,
  type FillCandidatesResult,
  type Household,
  type HouseholdBookRequestInput,
  type HouseholdSlotsRequestInput,
  type HouseholdSlotsResult,
  type ListContact,
  type ListContactCreate,
  type ListRemoveRequest,
  type ListRestoreRequest,
  type SeriesRequestInput,
  type SeriesResult,
} from '@velora/contracts/schedule';
import { buildPath, type HttpClient } from '@velora/sdk';
import { z } from 'zod';
import {
  AppointmentDetailSchema,
  AppointmentProceduresResultSchema,
  PlannedProceduresResultSchema,
  type AppointmentProceduresResult,
  type AttachProceduresRequestInput,
  type DetachProceduresRequest,
  type PlannedProceduresResult,
  CompoundMoveResultWithActionSchema,
  DayNoteResultSchema,
  MoveResultWithActionSchema,
  OpeningsResultSchema,
  PanelScheduleContextSchema,
  PinboardListSchema,
  PinboardResultSchema,
  RecallDraftsResultSchema,
  ScheduleListSchema,
  ScheduleWriteResultSchema,
  UndoResultSchema,
  type AppointmentDetail,
  type AppointmentPatchRequest,
  type CompoundMoveRequest,
  type CompoundMoveResult,
  type BreakRequestInput,
  type CreateAppointmentRequestInput,
  type DayNote,
  type MoveResultWithAction,
  type OpeningsResult,
  type PanelScheduleContext,
  type PinboardList,
  type PinboardMode,
  type PinboardResult,
  type PlaceRequest,
  type RecallDraftsRequest,
  type RecallDraftsResult,
  type ScheduleList,
  type ScheduleListName,
  type ScheduleWriteResult,
  type StatusChangeRequest,
  type UndoResult,
} from './contract.js';

export interface RangeArgs {
  locationId: string;
  from: string;
  to: string;
}

/** A live-delta range read: only these appointments, no blockouts or leases. */
export interface RangeDeltaArgs extends RangeArgs {
  appointmentIds: readonly string[];
}

export interface OpeningsArgs {
  locationId: string;
  lengthMin: number;
  timePattern?: string | undefined;
  providerId?: string | undefined;
  operatoryIds?: readonly string[] | undefined;
  /** First office-local day to search (default today). */
  from?: string | undefined;
  days?: number | undefined;
  partOfDay?: 'any' | 'am' | 'pm' | undefined;
  /** 0 = Sunday. */
  weekdays?: readonly number[] | undefined;
  /** Moving this appointment: its own slot is not busy. */
  appointmentId?: string | undefined;
  /** The patient being booked (patient grade prime-time policy marks slots). */
  patientId?: string | undefined;
  appointmentTypeId?: string | undefined;
  /** 1..20. */
  limit?: number | undefined;
}

export interface ScheduleApi {
  range(args: RangeArgs, signal?: AbortSignal): Promise<ScheduleRange>;
  /** The named appointments that are still in the range (live delta). */
  rangeDelta(args: RangeDeltaArgs, signal?: AbortSignal): Promise<ScheduleRange>;
  context(args: RangeArgs, signal?: AbortSignal): Promise<PanelScheduleContext>;
  detail(id: string, signal?: AbortSignal): Promise<AppointmentDetail>;
  pinboard(
    args: { locationId: string; scope: 'mine' | 'team'; q?: string },
    signal?: AbortSignal,
  ): Promise<PinboardList>;
  list(
    args: {
      locationId: string;
      list: ScheduleListName;
      q?: string;
      includeArchived?: boolean;
      limit?: number;
      offset?: number;
    },
    signal?: AbortSignal,
  ): Promise<ScheduleList>;
  /* NEW-WF-05: family and series booking, ASAP entries, working the lists. */
  household(
    args: { locationId: string; patientId: string },
    signal?: AbortSignal,
  ): Promise<Household>;
  householdSlots(body: HouseholdSlotsRequestInput): Promise<HouseholdSlotsResult>;
  bookHousehold(body: HouseholdBookRequestInput): Promise<CompoundCreateResult>;
  seriesPreview(body: SeriesRequestInput): Promise<SeriesResult>;
  bookSeries(body: SeriesRequestInput): Promise<SeriesResult>;
  createAsap(body: AsapEntryCreateInput): Promise<AsapEntryResult>;
  removeFromList(body: ListRemoveRequest): Promise<{ ok: true }>;
  restoreToList(body: ListRestoreRequest): Promise<{ ok: true }>;
  logContact(body: ListContactCreate): Promise<{ contact: ListContact }>;
  /** Text an opening to the ASAP list: preview, then send (staff-triggered). */
  asapTextPreview(body: AsapTextOpening): Promise<AsapTextPreview>;
  asapTextSend(body: AsapTextSendRequest): Promise<AsapTextSendResult>;
  fillCandidates(
    args: { locationId: string; date: string },
    signal?: AbortSignal,
  ): Promise<FillCandidatesResult>;
  openings(args: OpeningsArgs, signal?: AbortSignal): Promise<OpeningsResult>;
  /**
   * Patient picker search. `scope: 'all'` searches every office the user
   * can see (role test FD-15: a patient of another office calling this one);
   * the default is this office's patients.
   */
  searchPatients(
    q: string,
    signal?: AbortSignal,
    scope?: 'office' | 'all',
  ): Promise<z.infer<typeof PatientSearchResponseSchema>>;
  status(id: string, body: StatusChangeRequest): Promise<ScheduleWriteResult>;
  breakAppointment(id: string, body: BreakRequestInput): Promise<ScheduleWriteResult>;
  patch(id: string, body: AppointmentPatchRequest): Promise<ScheduleWriteResult>;
  move(id: string, body: MoveRequest): Promise<MoveResultWithAction>;
  /** Move a booked appointment to another office (role test fix 2); needs schedule.edit in both. */
  transfer(id: string, body: TransferRequest): Promise<TransferResult>;
  /** Several appointments as one compound command (multi-select drag, SM-15). */
  moveMany(body: CompoundMoveRequest): Promise<CompoundMoveResult>;
  create(body: CreateAppointmentRequestInput): Promise<ScheduleWriteResult>;
  pin(
    id: string,
    body: { idempotencyKey: string; expectedVersion: number; mode: PinboardMode; note?: string },
  ): Promise<PinboardResult>;
  unpin(pinId: string, idempotencyKey: string): Promise<PinboardResult>;
  place(pinId: string, body: PlaceRequest): Promise<PinboardResult>;
  restore(pinId: string, idempotencyKey: string): Promise<PinboardResult>;
  undo(actionId: string, idempotencyKey: string): Promise<UndoResult>;
  saveDayNote(args: { locationId: string; day: string; body: string }): Promise<DayNote | null>;
  /** The verdict Save would get (same rules as the write; SB-05, SB-06). */
  check(body: SlotCheckRequestInput, signal?: AbortSignal): Promise<SlotCheckResult>;
  /** Holds a slot while it is being booked (SB-13); 409 when someone else holds or booked it. */
  takeLease(body: SlotLeaseRequestInput): Promise<SlotLease>;
  releaseLease(id: string): Promise<void>;
  /** NEW-WF-01: the patient's treatment-planned rows for the panel's "Add from treatment plan". */
  plannedProcedures(
    patientId: string,
    locationId: string,
    signal?: AbortSignal,
  ): Promise<PlannedProceduresResult>;
  attachProcedures(
    id: string,
    body: AttachProceduresRequestInput,
  ): Promise<AppointmentProceduresResult>;
  detachProcedures(id: string, body: DetachProceduresRequest): Promise<AppointmentProceduresResult>;
  /** OD Make Recall / Make Fam Recall: recall-coded pinboard items (WF-09). */
  recallDrafts(body: RecallDraftsRequest): Promise<RecallDraftsResult>;
  /** "Do this in Dentrix": a gated change becomes a front-desk task. */
  handoff(body: PmsHandoffRequestInput): Promise<PmsHandoffResult>;
  /** Appointment Book views (Dentrix "Views"), personal and shared, no fixed limit. */
  savedViews(locationId: string, signal?: AbortSignal): Promise<SavedViewsResult>;
  createSavedView(body: SavedViewCreateInput): Promise<ScheduleSavedView>;
  patchSavedView(id: string, body: SavedViewPatchInput): Promise<ScheduleSavedView>;
  deleteSavedView(id: string): Promise<void>;
}

const signalOf = (signal?: AbortSignal) => (signal ? { signal } : {});

export function createScheduleApi(http: HttpClient, allOfficesHttp?: HttpClient): ScheduleApi {
  const post = <T>(path: string, body: unknown, schema: z.ZodType<T>) =>
    http.request(path, { method: 'POST', body, schema });
  return {
    range: (a, signal) =>
      http.request(
        buildPath('/schedule/appointments', undefined, {
          locationId: a.locationId,
          from: a.from,
          to: a.to,
        }),
        { schema: ScheduleRangeSchema, ...signalOf(signal) },
      ),
    rangeDelta: (a, signal) =>
      http.request(
        buildPath('/schedule/appointments', undefined, {
          locationId: a.locationId,
          from: a.from,
          to: a.to,
          appointmentIds: a.appointmentIds.join(','),
          includeBlockouts: 'false',
          includeLeases: 'false',
        }),
        { schema: ScheduleRangeSchema, ...signalOf(signal) },
      ),
    context: (a, signal) =>
      http.request(buildPath('/schedule/context', undefined, { ...a }), {
        schema: PanelScheduleContextSchema,
        ...signalOf(signal),
      }),
    detail: (id, signal) =>
      http.request(buildPath('/schedule/appointments/:id', { id }), {
        schema: AppointmentDetailSchema,
        ...signalOf(signal),
      }),
    pinboard: (a, signal) =>
      http.request(buildPath('/schedule/pinboard', undefined, { ...a }), {
        schema: PinboardListSchema,
        ...signalOf(signal),
      }),
    list: (a, signal) =>
      http.request(
        buildPath('/schedule/lists', undefined, {
          locationId: a.locationId,
          list: a.list,
          q: a.q,
          includeArchived: a.includeArchived ? 'true' : undefined,
          limit: a.limit,
          offset: a.offset,
        }),
        { schema: ScheduleListSchema, ...signalOf(signal) },
      ),
    household: (a, signal) =>
      http.request(buildPath('/schedule/household', undefined, { ...a }), {
        schema: HouseholdSchema,
        ...signalOf(signal),
      }),
    householdSlots: (body) => post('/schedule/household/slots', body, HouseholdSlotsResultSchema),
    bookHousehold: (body) =>
      post('/schedule/appointments/household', body, CompoundCreateResultSchema),
    seriesPreview: (body) =>
      post('/schedule/appointments/series/preview', body, SeriesResultSchema),
    bookSeries: (body) => post('/schedule/appointments/series', body, SeriesResultSchema),
    createAsap: (body) => post('/schedule/asap', body, AsapEntryResultSchema),
    removeFromList: (body) => post('/schedule/lists/remove', body, ListChangeResultSchema),
    restoreToList: (body) => post('/schedule/lists/restore', body, ListChangeResultSchema),
    logContact: (body) => post('/schedule/lists/contacts', body, ListContactResultSchema),
    asapTextPreview: (body) => post('/schedule/asap-texts/preview', body, AsapTextPreviewSchema),
    asapTextSend: (body) => post('/schedule/asap-texts/send', body, AsapTextSendResultSchema),
    fillCandidates: (a, signal) =>
      http.request(buildPath('/schedule/fill-candidates', undefined, { ...a }), {
        schema: FillCandidatesResultSchema,
        ...signalOf(signal),
      }),
    openings: (a, signal) =>
      http.request(
        buildPath('/schedule/openings', undefined, {
          locationId: a.locationId,
          lengthMin: a.lengthMin,
          timePattern: a.timePattern,
          providerId: a.providerId,
          operatoryIds: a.operatoryIds?.length ? a.operatoryIds.join(',') : undefined,
          from: a.from,
          days: a.days,
          partOfDay: a.partOfDay,
          weekdays: a.weekdays?.length ? a.weekdays.join(',') : undefined,
          appointmentId: a.appointmentId,
          patientId: a.patientId,
          appointmentTypeId: a.appointmentTypeId,
          limit: a.limit,
        }),
        { schema: OpeningsResultSchema, ...signalOf(signal) },
      ),
    // The patients module's search: the query travels in the body (never a
    // URL); `http` carries the schedule's office, so hits are that office's patients.
    searchPatients: (q, signal, scope) =>
      (scope === 'all' && allOfficesHttp ? allOfficesHttp : http).request('/patients/search', {
        method: 'POST',
        body: { q, mode: 'auto', includeInactive: false, homeLocationOnly: false, limit: 8 },
        schema: PatientSearchResponseSchema,
        ...signalOf(signal),
      }),
    status: (id, body) =>
      post(buildPath('/schedule/appointments/:id/status', { id }), body, ScheduleWriteResultSchema),
    breakAppointment: (id, body) =>
      post(buildPath('/schedule/appointments/:id/break', { id }), body, ScheduleWriteResultSchema),
    handoff: (body) => post('/schedule/pms-handoffs', body, PmsHandoffResultSchema),
    savedViews: (locationId, signal) =>
      http.request(buildPath('/schedule/saved-views', undefined, { locationId }), {
        schema: SavedViewsResultSchema,
        ...signalOf(signal),
      }),
    createSavedView: (body) => post('/schedule/saved-views', body, ScheduleSavedViewSchema),
    patchSavedView: (id, body) =>
      http.request(buildPath('/schedule/saved-views/:id', { id }), {
        method: 'PATCH',
        body,
        schema: ScheduleSavedViewSchema,
      }),
    deleteSavedView: async (id) => {
      await http.request(buildPath('/schedule/saved-views/:id', { id }), {
        method: 'DELETE',
        schema: z.unknown(),
      });
    },
    patch: (id, body) =>
      http.request(buildPath('/schedule/appointments/:id', { id }), {
        method: 'PATCH',
        body,
        schema: ScheduleWriteResultSchema,
      }),
    move: (id, body) =>
      post(buildPath('/schedule/appointments/:id/move', { id }), body, MoveResultWithActionSchema),
    transfer: (id, body) =>
      post(buildPath('/schedule/appointments/:id/transfer', { id }), body, TransferResultSchema),
    moveMany: (body) =>
      post('/schedule/appointments/move', body, CompoundMoveResultWithActionSchema),
    create: (body) => post('/schedule/appointments', body, ScheduleWriteResultSchema),
    pin: (id, body) =>
      post(buildPath('/schedule/appointments/:id/pin', { id }), body, PinboardResultSchema),
    unpin: (pinId, idempotencyKey) =>
      http.request(buildPath('/schedule/pinboard/:id', { id: pinId }, { idempotencyKey }), {
        method: 'DELETE',
        schema: PinboardResultSchema,
      }),
    place: (pinId, body) =>
      post(buildPath('/schedule/pinboard/:id/place', { id: pinId }), body, PinboardResultSchema),
    restore: (pinId, idempotencyKey) =>
      post(
        buildPath('/schedule/pinboard/:id/restore', { id: pinId }),
        { idempotencyKey },
        PinboardResultSchema,
      ),
    undo: (actionId, idempotencyKey) =>
      post(
        buildPath('/schedule/actions/:id/undo', { id: actionId }),
        { idempotencyKey },
        UndoResultSchema,
      ),
    saveDayNote: async (a) => {
      const res = await http.request('/schedule/day-notes', {
        method: 'PUT',
        body: { locationId: a.locationId, day: a.day, operatoryId: null, body: a.body },
        schema: DayNoteResultSchema,
      });
      return res.note;
    },
    check: (body, signal) =>
      http.request('/schedule/appointments/check', {
        method: 'POST',
        body,
        schema: SlotCheckResultSchema,
        ...signalOf(signal),
      }),
    takeLease: (body) => post('/schedule/leases', body, SlotLeaseSchema),
    releaseLease: async (id) => {
      await http.request(buildPath('/schedule/leases/:id', { id }), {
        method: 'DELETE',
        schema: z.unknown(),
      });
    },
    plannedProcedures: (patientId, locationId, signal) =>
      http.request(
        buildPath('/schedule/patients/:id/planned-procedures', { id: patientId }, { locationId }),
        { schema: PlannedProceduresResultSchema, ...signalOf(signal) },
      ),
    attachProcedures: (id, body) =>
      post(
        buildPath('/schedule/appointments/:id/procedures', { id }),
        body,
        AppointmentProceduresResultSchema,
      ),
    detachProcedures: (id, body) =>
      http.request(buildPath('/schedule/appointments/:id/procedures', { id }), {
        method: 'DELETE',
        body,
        schema: AppointmentProceduresResultSchema,
      }),
    recallDrafts: (body) => post('/schedule/pinboard/drafts', body, RecallDraftsResultSchema),
  };
}

/** One idempotency key per user intent (BUILD-PLAN 2.5.3: `vel-<uuid>`). */
export function intentKey(): string {
  const uuid =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `vel-${uuid}`;
}
